// Admin authentication hook
import { useMemo } from "react";
import { useSession, isUserAdmin, ADMIN_EMAILS } from "@/lib/auth-client";

/**
 * Admin auth state returned by useAdminAuth
 */
export interface AdminAuthState {
  /** Current user object or null if not logged in */
  user: {
    id: string;
    email: string;
    name: string | null;
    image: string | null;
    isAdmin?: boolean;
  } | null;
  /** Whether the current user is an admin */
  isAdmin: boolean;
  /** Whether the auth state is loading */
  isLoading: boolean;
  /** Any error that occurred during auth */
  error: Error | null;
  /** Whether the user is authenticated (logged in) */
  isAuthenticated: boolean;
}

/**
 * Hook to check admin authentication status
 * Combines useSession with admin verification
 *
 * @returns AdminAuthState object with user, isAdmin, isLoading, and error
 *
 * @example
 * ```tsx
 * function AdminPage() {
 *   const { user, isAdmin, isLoading, error } = useAdminAuth();
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *   if (!isAdmin) return <Unauthorized />;
 *
 *   return <AdminContent user={user} />;
 * }
 * ```
 */
export function useAdminAuth(): AdminAuthState {
  const { data: session, isPending, error } = useSession();

  const state = useMemo<AdminAuthState>(() => {
    const user = session?.user ?? null;

    return {
      user,
      isAdmin: isUserAdmin(user),
      isLoading: isPending,
      error: error instanceof Error ? error : error ? new Error(String(error)) : null,
      isAuthenticated: !!user,
    };
  }, [session, isPending, error]);

  return state;
}

/**
 * Hook to require admin access
 * Throws if not admin (use with error boundary)
 *
 * @returns The admin user (never null)
 * @throws Error if not authenticated or not admin
 */
export function useRequireAdmin() {
  const { user, isAdmin, isLoading, error } = useAdminAuth();

  if (isLoading) {
    return { user: null, isLoading: true as const };
  }

  if (error) {
    throw error;
  }

  if (!user) {
    throw new Error("Authentication required");
  }

  if (!isAdmin) {
    throw new Error("Admin access required");
  }

  return { user, isLoading: false as const };
}

/**
 * Re-export admin emails list for convenience
 */
export { ADMIN_EMAILS };
